import { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import EcomEngineAPI from 'api/ecom_engine_api';
import { getAccountCreationSettings, getParamFromUrl } from 'utils/helpers';
import PasswordField from 'components/formik/password_field/password_field';
import LoadingButton from 'components/loading_button';
import { discardBooking, fetchCurrent } from 'redux/actions/booking';
import { setFerryGrouping } from 'redux/actions/ui/sealink';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { getFirstFerryGrouping } from 'redux/selectors/ui/sealink';
import { setUser } from 'redux/slice/user';
import { useDocumentTitle } from 'tenants/sealink/utils/use_document_title';
import { isRegisteredVehiclesOnly } from '../../ferry_flow/vehicle_form/helpers';
import { formSchema, handleLoginSucceedRedirect } from './helpers';
import LoginAlertModal from './login_alert_modal';

const Login = ({ showTitle = true, successRedirectURL }) => {
  useDocumentTitle('Login');
  const [loginAlert, setLoginAlert] = useState('');
  const vehicleConfig = useSelector(
    (state) => state?.sealink?.vehicle_config || {}
  );
  const {
    registeredVehiclesOnly: cmsRegisteredVehiclesOnly = false,
    registeredVehiclesOnlyWarningOnLogin = null,
  } = vehicleConfig;
  const { email: defaultEmail } = getParamFromUrl();
  const currentBooking = useSelector(getCurrentBooking);
  const dispatch = useDispatch();
  const history = useHistory();
  const [submissionMessage, setSubmissionMessage] = useState('');
  const hasVehicles = !isEmpty(currentBooking.vehicles_attributes);
  const vehicleForm = useSelector(getFirstFerryGrouping)?.vehicleForm;
  const [showVehicleWarning, setShowVehicleWarning] = useState(
    hasVehicles &&
      cmsRegisteredVehiclesOnly &&
      registeredVehiclesOnlyWarningOnLogin
  );

  const onSubmit = async (params, { setSubmitting, isSubmitting }) => {
    if (isSubmitting) {
      return;
    }

    setSubmitting(true);
    setSubmissionMessage(null);
    setShowVehicleWarning(false);

    const result = await EcomEngineAPI.account.login(params);

    setSubmitting(false);

    if (result.status === 401) {
      setSubmissionMessage('Invalid login credential');
    }

    if (result.message) {
      setSubmissionMessage(result.message);
    }

    if (result.id) {
      if (isRegisteredVehiclesOnly(vehicleConfig, result)) {
        // In case user login from the services page, we need to clear the form data
        if (vehicleForm) {
          dispatch(setFerryGrouping([]));
        }

        // Remove the whole booking if has vehicles
        if (hasVehicles) {
          await dispatch(discardBooking());
        }
      }

      dispatch(setUser(result));
      dispatch(fetchCurrent());

      // Show confirmation modal if has login alert
      if (result.alerts?.login) {
        setLoginAlert(result.alerts.login);
        return;
      }

      handleLoginSucceedRedirect(successRedirectURL, history);
    }
  };
  const { enabled: accountCreationEnabled } = getAccountCreationSettings();
  const loginContent = ecom.craftConfig?.features?.loginSettings?.loginContent;

  return (
    <div>
      <LoginAlertModal
        loginAlert={loginAlert}
        setLoginAlert={setLoginAlert}
        onContinue={() => {
          setLoginAlert('');
          handleLoginSucceedRedirect(successRedirectURL, history);
        }}
      />
      <div className="booking-login d-flex flex-column gap-3">
        {showTitle && <h3>Log into your account</h3>}
        {loginContent && <div className="content">{parse(loginContent)}</div>}

        <Formik
          initialValues={{
            username: defaultEmail || '',
            password: '',
          }}
          validationSchema={formSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div>
                {showVehicleWarning && (
                  <Alert
                    variant="danger"
                    onClose={() => setShowVehicleWarning(false)}
                    dismissible
                  >
                    {registeredVehiclesOnlyWarningOnLogin}
                  </Alert>
                )}
                {submissionMessage && (
                  <Alert
                    variant="danger"
                    onClose={() => setSubmissionMessage(null)}
                    dismissible
                  >
                    {submissionMessage}
                  </Alert>
                )}
                <label htmlFor="username" className="form-label">
                  Username / Email
                  <span className="booking-login__hint">(required)</span>
                </label>
                <Field
                  name="username"
                  className="form-control"
                  id="username"
                  placeholder="Username or Email"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="username"
                  component="div"
                  id="username_error"
                />
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Password
                  <span className="booking-login__hint">(required)</span>
                </label>
                <PasswordField
                  name="password"
                  id="password"
                  placeholder="Password"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="password"
                  component="div"
                  id="password_error"
                />
              </div>

              <div>
                <Link className="text-small" to="/account/reset-password">
                  Reset my password
                </Link>
              </div>
              <LoadingButton
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-100"
                loading={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Login'}
              </LoadingButton>
              {accountCreationEnabled && (
                <div>
                  <Link className="text-small" to="/account/create-account">
                    Create an account{' '}
                    <span className="badge ms-1 bg-success">New</span>
                  </Link>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

Login.propTypes = {
  showTitle: PropTypes.bool,
  successRedirectURL: PropTypes.string,
};

export default Login;
